/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */
import { useCallback } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import type { CityStats, GameStatus, Grid, NewsItem, ResearchState } from '../types';
import { INITIAL_MONEY, DEFAULT_UNLOCKED } from '../constants';
import { resolvePlayAudio } from '../src/adapters/ui/hooks/useAudio';
import { gameStore } from '../stores/gameStore';
import { simulationEngine } from '../src/domain/services/SimulationEngine';
import {
  createPlatformSession,
  ensurePlatformSession,
  getOrCreatePlayerRef,
} from '../src/adapters/ui/hooks/usePlatformPersistence';
import { startScenarioUseCase } from '../src/infrastructure/di/container';
import type { ActiveModal } from '../components/UIOverlay';

const CATEGORY_MESSAGES: Record<string, string> = {
  fintech:
    'Welcome to FinTech. Navigate digital banking, payments, and crypto while mastering AML/KYC compliance.',
  healthtech:
    'Welcome to HealthTech. Protect patient data while innovating in digital health and HIPAA compliance.',
  saas_b2b:
    'Welcome to B2B SaaS. Build enterprise-grade software with SOC 2 and ISO 27001 certifications.',
  sandbox: 'Architect, the board is watching. Build a secure foundation before you scale.',
};

export interface UseGameSessionHandlersDeps {
  gameStarted: boolean;
  activeScenario: { id: string } | null;
  setShowLanding: (value: boolean) => void;
  setGameStarted: (value: boolean) => void;
  setGameStatus: (status: GameStatus) => void;
  setAiEnabled: (value: boolean) => void;
  setGrid: Dispatch<SetStateAction<Grid>>;
  setStats: Dispatch<SetStateAction<CityStats>>;
  setResearch: Dispatch<SetStateAction<ResearchState>>;
  setNewsFeed: Dispatch<SetStateAction<NewsItem[]>>;
  setActiveModal: (modal: ActiveModal) => void;
  setActiveScenario: (scenario: null) => void;
  setGameSpeed: Dispatch<SetStateAction<number>>;
  saveGame: () => Promise<void>;
  loadGame: () => Promise<boolean>;
  refreshSavedGameFlag: () => void;
  loadScenario: (scenarioId: string, triggerStory: (customText?: string) => Promise<void>) => void;
  triggerStory: (customText?: string) => Promise<void>;
  addNewsItem: (item: NewsItem) => void;
}

export function useGameSessionHandlers({
  gameStarted,
  activeScenario,
  setShowLanding,
  setGameStarted,
  setGameStatus,
  setAiEnabled,
  setGrid,
  setStats,
  setResearch,
  setNewsFeed,
  setActiveModal,
  setActiveScenario,
  setGameSpeed,
  saveGame,
  loadGame,
  refreshSavedGameFlag,
  loadScenario,
  triggerStory,
  addNewsItem,
}: UseGameSessionHandlersDeps) {
  const handleSoftReset = useCallback(() => {
    if (gameStarted && !activeScenario) {
      void saveGame();
    }
    resolvePlayAudio({ type: 'stop_ambient' });
    setGameStarted(false);
    setShowLanding(false);
    setActiveModal('none');
    setGameStatus('playing');
    setGrid(simulationEngine.createInitialGrid());
    setActiveScenario(null);
    gameStore.getState().setTickNumber(0);
    setStats({
      budget: INITIAL_MONEY,
      compliance: 85,
      threat: 0,
      threatHistory: Array(20).fill(0),
      day: 1,
      loanCount: 0,
      lastPitchDay: -10,
      activeProducts: [],
    });
    setResearch({
      unlockedBuildings: DEFAULT_UNLOCKED,
      completedTechs: [],
      activeResearchId: null,
      researchProgress: 0,
      currentMaxLevel: 1,
    });
    setNewsFeed([]);
    refreshSavedGameFlag();
  }, [
    activeScenario,
    gameStarted,
    refreshSavedGameFlag,
    saveGame,
    setActiveModal,
    setActiveScenario,
    setGameStarted,
    setGameStatus,
    setGrid,
    setNewsFeed,
    setResearch,
    setShowLanding,
    setStats,
  ]);

  const handleExtend = useCallback(() => {
    setGameStatus('playing');
    setStats((prev) => ({
      ...prev,
      compliance: 50,
      threat: 20,
      budget: prev.budget + 2000,
      loanCount: prev.loanCount + 2,
    }));
    addNewsItem({
      id: Date.now().toString(),
      text: 'EMERGENCY BAILOUT APPROVED. TIMELINE EXTENDED.',
      type: 'neutral',
    });
    resolvePlayAudio({ type: 'sfx', sound: 'success' });
  }, [addNewsItem, setGameStatus, setStats]);

  const handleStart = useCallback(
    (enabled: boolean, scenarioId?: string, industryCategory?: string) => {
      const result = startScenarioUseCase.execute({
        aiEnabled: enabled,
        scenarioId,
        industryCategory,
      });

      if (!result.success) {
        console.error('StartScenario rejected:', result.errors);
        return;
      }

      resolvePlayAudio({ type: 'init' });
      resolvePlayAudio({ type: 'start_ambient' });
      setAiEnabled(result.aiEnabled);

      if (result.mode === 'scenario' && result.scenarioId) {
        loadScenario(result.scenarioId, triggerStory);
        return;
      }

      if (result.industryCategory) {
        localStorage.setItem('compliance_command_industry', result.industryCategory);
      }

      gameStore.getState().setTickNumber(0);
      setGameStarted(true);
      void createPlatformSession(result.industryCategory ?? 'sandbox', getOrCreatePlayerRef());
      triggerStory(
        CATEGORY_MESSAGES[result.industryCategory ?? 'sandbox'] ?? CATEGORY_MESSAGES.sandbox,
      );
      void saveGame();
    },
    [loadScenario, saveGame, setAiEnabled, setGameStarted, triggerStory],
  );

  const handleResume = useCallback(async () => {
    resolvePlayAudio({ type: 'init' });
    const loaded = await loadGame();
    if (!loaded) {
      refreshSavedGameFlag();
      if (typeof window !== 'undefined') {
        window.alert('Save file missing or corrupted. Start a new sandbox simulation.');
      }
      return;
    }
    setGameSpeed(0);
    const industry =
      (typeof localStorage !== 'undefined' && localStorage.getItem('compliance_command_industry')) ||
      'sandbox';
    await ensurePlatformSession(industry);
    await saveGame();
  }, [loadGame, refreshSavedGameFlag, saveGame, setGameSpeed]);

  return { handleSoftReset, handleExtend, handleStart, handleResume };
}